<template>
    <div class="photo-grid-container">
        <div class="d-flex align-center justify-space-between mb-3">
            <h4 style="font-weight: 600;">Fotos recibidas:</h4>
            <v-btn 
                v-if="photos.length > 0"
                color="error" 
                outlined 
                small
                @click="showDeleteAllDialog = true"
            >
                <v-icon left small>mdi-delete-sweep</v-icon>
                Eliminar todas
            </v-btn>
        </div>
        
        <draggable 
            v-model="localPhotos" 
            class="photos-grid d-flex flex-wrap pa-2" 
            style="gap: 12px;"
            tag="div"
            v-bind="dragOptions"
            @start="drag = true"
            @end="onDragEnd"
        >
            <transition-group type="transition" :name="!drag ? 'flip-list' : null"
            class="d-flex flex-wrap"
            style="gap: 12px;"
            >
                <div v-for="(photo, index) in localPhotos" :key="photo.id" class="photo-item">
                    <v-card class="photo-card" elevation="0"
                        style="border-radius: 8px !important; width: 220px; height: 340px; position: relative; overflow: hidden; cursor: pointer;"
                        @click="$emit('photo-click', photo)">
                        
                        <!-- Indicador de carga -->
                        <div v-if="!imageLoaded.includes(photo.id) && !imageErrors.includes(photo.id)" 
                             class="photo-loading d-flex align-center justify-center"
                             style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-color: #f5f5f5; z-index: 2;">
                            <v-progress-circular indeterminate color="primary" size="20" width="2"></v-progress-circular>
                        </div>
                        
                        <!-- Imagen real -->
                        <img 
                            :src="getPhotoUrl(photo.id)" 
                            :alt="`Foto ${photo.order}`"
                            class="photo-image"
                            :class="{ 'loaded': imageLoaded.includes(photo.id) }"
                            @load="handleImageLoad(photo.id)"
                            @error="handleImageError(photo.id)"
                            style="width: 100%; height: 100%; object-fit: cover; transition: opacity 0.3s ease; opacity: 0;"
                        />
                        
                        <!-- Placeholder de error -->
                        <div v-if="imageErrors.includes(photo.id)" 
                             class="photo-error d-flex flex-column align-center justify-center"
                             style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-color: #f5f5f5; z-index: 2;">
                            <lucide-image :size="24" color="#999"></lucide-image>
                            <span style="font-size: 10px; color: #dc3545; margin-top: 4px;">Error</span>
                        </div>
                        
                        <div class="photo-order"
                            style="position: absolute; top: 8px; left: 14px; background: rgba(0,0,0,0.7); color: white; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; z-index: 3;">
                            {{ photo.order }}
                        </div>

                        <!-- Botón de borrar -->
                        <v-btn 
                            icon 
                            x-small 
                            class="delete-button"
                            style="position: absolute; top: 8px; right: 10px; background: rgba(220, 53, 69, 0.9); color: white; z-index: 4; width: 30px; height: 30px;"
                            @click.stop="$emit('delete-photo', photo)"
                        >
                            <v-icon small>mdi-delete-outline</v-icon>
                        </v-btn>
                    </v-card>
                </div>
            </transition-group>
        </draggable>

        <!-- Diálogo de confirmación para eliminar todas las fotos -->
        <v-dialog v-model="showDeleteAllDialog" max-width="400">
            <v-card>
                <v-card-title class="headline">
                    <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
                    Confirmar eliminación
                </v-card-title>
                
                <v-card-text>
                    ¿Estás seguro de que quieres eliminar todas las fotos? Esta acción no se puede deshacer.
                    <br><br>
                    <strong>{{ photos.length }} foto{{ photos.length !== 1 ? 's' : '' }} será{{ photos.length !== 1 ? 'n' : '' }} eliminada{{ photos.length !== 1 ? 's' : '' }}.</strong>
                </v-card-text>
                
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn 
                        color="grey" 
                        text 
                        @click="showDeleteAllDialog = false"
                    >
                        Cancelar
                    </v-btn>
                    <v-btn 
                        color="error" 
                        @click="confirmDeleteAll"
                    >
                        Eliminar todas
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { Image } from 'lucide-vue'
import draggable from 'vuedraggable'

export default {
    name: 'PhotoGrid',
    components: {
        LucideImage: Image,
        draggable
    },
    props: {
        photos: {
            type: Array,
            default: () => []
        },
        sessionId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            drag: false,
            imageLoaded: [],
            imageErrors: [],
            showDeleteAllDialog: false
        }
    },
    computed: {
        localPhotos: {
            get() {
                return this.photos
            },
            set(value) {
                this.$emit('photos-reordered', value)
            }
        },
        dragOptions() {
            return {
                animation: 200,
                group: "photos",
                disabled: false,
                ghostClass: "ghost"
            }
        }
    },
    methods: {
        onDragEnd() {
            this.drag = false
            this.$emit('drag-end')
        },

        getPhotoUrl(photoId) {
            return process.env.VUE_APP_AXIOS_URI + `/qr-upload/image/${this.sessionId}/${photoId}`
        },

        handleImageLoad(photoId) {
            // Remover de errores si estaba ahí
            this.imageErrors = this.imageErrors.filter(id => id !== photoId)
            // Agregar a cargadas si no está ya
            if (!this.imageLoaded.includes(photoId)) {
                this.imageLoaded.push(photoId)
            }
        },

        handleImageError(photoId) {
            // Remover de cargadas si estaba ahí
            this.imageLoaded = this.imageLoaded.filter(id => id !== photoId)
            // Agregar a errores si no está ya
            if (!this.imageErrors.includes(photoId)) {
                this.imageErrors.push(photoId)
            }
        },

        confirmDeleteAll() {
            // Emitir evento para que el componente padre maneje la eliminación
            this.$emit('delete-all-photos')
            this.showDeleteAllDialog = false
        }
    },
    watch: {
        photos: {
            handler(newPhotos) {
                // Limpiar estados de imágenes para fotos que ya no existen
                const currentPhotoIds = newPhotos.map(photo => photo.id)
                this.imageLoaded = this.imageLoaded.filter(id => currentPhotoIds.includes(id))
                this.imageErrors = this.imageErrors.filter(id => currentPhotoIds.includes(id))
            },
            deep: true
        }
    }
}
</script>

<style scoped>
.photo-grid-container {
    width: 100%;
}

.photos-grid {
    overflow-y: hidden;
}

.photo-card {
    transition: transform 0.2s ease;
    cursor: grab;
    border: 1px solid #e0e0e0;
}

.photo-card:active {
    cursor: grabbing;
}

.photo-image.loaded {
    opacity: 1 !important;
}

.delete-button {
    opacity: 1;
    transition: all 0.2s ease;
}

.delete-button:hover {
    background-color: rgba(220, 53, 69, 1) !important;
}

/* Vue.Draggable classes */
.ghost {
    opacity: 0.3;
    background: rgba(77, 212, 83, 0.2);
    border: 2px dashed #4dd453;
}

/* Transition classes */
.flip-list-move {
    transition: transform 0.5s;
}

.no-move {
    transition: transform 0s;
}
</style> 